import React, { useState } from "react";
import { Crown, Copy, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCruise } from "../context/CruiseContext";
import { Button } from "./ui/button";

const Honoree = () => {
  const { honoree } = useCruise();
  const [copied, setCopied] = useState(false);
  if (!honoree) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(honoree.promoCode);
    } catch (e) {
      /* clipboard unavailable */
    }
    setCopied(true);
    toast.success(`Promo code ${honoree.promoCode} copied`);
    setTimeout(() => setCopied(false), 2000);
  };

  const goBook = () => {
    window.dispatchEvent(new CustomEvent("hh-promo", { detail: honoree.promoCode }));
    document.getElementById("group")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="honoree" data-testid="honoree-section" className="relative bg-[#0A0A0A] py-20 sm:py-28 overflow-hidden">
      <div className="absolute -left-40 top-10 w-[520px] h-[520px] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
      <div className="absolute -right-40 bottom-0 w-[480px] h-[480px] rounded-full bg-[var(--hh-gold)]/10 blur-[120px]" />
      <div className="relative max-w-6xl mx-auto px-5 grid md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-10 lg:gap-16 items-center">
        <div className="relative mx-auto w-full max-w-sm">
          <div className="absolute -inset-3 rounded-[28px] bg-gradient-to-br from-[#D4AF37] via-[var(--hh-gold)] to-[#0f5a2a] opacity-60 blur-xl" />
          <div className="relative rounded-3xl p-[3px] bg-gradient-to-br from-[#D4AF37] via-[var(--hh-gold)] to-[#D4AF37] shadow-[0_0_60px_rgba(212,175,55,0.35)]">
            <img
              data-testid="honoree-image"
              src={honoree.image}
              alt={`${honoree.name} celebrating her 80th birthday`}
              className="w-full h-auto rounded-[22px] block"
            />
          </div>
          <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black border border-[var(--hh-gold)]/60 rounded-full px-5 py-2 font-cond uppercase tracking-[0.3em] text-xs text-[var(--hh-gold)]">
            80 &amp; Fabulous
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 text-[var(--hh-gold)] mb-3">
            <Crown size={18} />
            <p className="font-cond uppercase tracking-[0.35em] text-sm" data-testid="honoree-kicker">{honoree.kicker}</p>
          </div>
          <h2 className="font-cond font-bold uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-none">
            {honoree.title}
          </h2>
          <p className="mt-3 font-cond uppercase tracking-widest text-[var(--hh-gold)] text-base sm:text-lg" data-testid="honoree-name">
            {honoree.name}
          </p>
          <p className="mt-5 text-white/75 text-base leading-relaxed max-w-xl" data-testid="honoree-caption">{honoree.caption}</p>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
            <button
              type="button"
              onClick={copy}
              data-testid="honoree-promo-copy-btn"
              className="group flex items-center justify-between gap-4 border-2 border-dashed border-[var(--hh-gold)] rounded-xl px-5 py-3 bg-[var(--hh-gold)]/5 hover:bg-[var(--hh-gold)]/15 transition-colors"
            >
              <span className="text-left">
                <span className="block text-[10px] uppercase tracking-[0.3em] text-white/60">{honoree.promoText}</span>
                <span className="block font-cond font-bold text-2xl tracking-[0.25em] text-[var(--hh-gold)]" data-testid="honoree-promo-code">
                  {honoree.promoCode}
                </span>
              </span>
              {copied ? <Check size={20} className="text-[var(--hh-gold)]" /> : <Copy size={20} className="text-white/70 group-hover:text-white" />}
            </button>
            <Button
              onClick={goBook}
              data-testid="honoree-book-btn"
              className="bg-[#E50914] hover:bg-[#b80710] text-white rounded-full px-7 py-6 font-cond uppercase tracking-widest"
            >
              Book with Her Code <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Honoree;
